/**
 * Embedded page (ADR-0045 R4) — the `page_ref` component renders ANOTHER page's
 * content INLINE. Composed with a `modal`/`drawer` container it reconstructs the
 * old overlay-page (a page reused as a dialog body), but as plain components.
 *
 * In the PORTAL the referenced page is fetched fresh and wrapped in its OWN
 * nested `PageRuntimeProvider` (own `$page`, own refresh, can self-close) — the
 * same isolation `PortalOverlay` gives an overlay page. In the BUILDER (editing /
 * running-canvas preview / no runtime) it shows a labelled placeholder: the page
 * is fetched as a PORTAL user, which the admin builder isn't, so the real content
 * only appears in the published portal.
 *
 * A depth context caps recursion so a page that (directly or transitively)
 * embeds itself can't render forever.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import { portalApi } from "@/lib/api-portal";

import { PageRenderer } from "./PageRenderer";
import { type PageRuntime, PageRuntimeProvider } from "./PageRuntime";

const MAX_EMBED_DEPTH = 4;
const EmbedDepth = createContext(0);

function Placeholder({ slug, note }: { slug: string; note?: string }) {
  return (
    <div className="rounded border border-dashed border-border bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
      Pagina «{slug || "—"}»{note ? ` · ${note}` : ""}
    </div>
  );
}

function EmbeddedLive({
  appSlug,
  slug,
  depth,
  input,
}: {
  appSlug: string;
  slug: string;
  depth: number;
  input: Record<string, unknown>;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["portal-page", appSlug, slug, "embed"],
    queryFn: () => portalApi.getPage(appSlug, slug),
    retry: false,
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Caricamento…</p>;
  if (error || !data) return <Placeholder slug={slug} note="impossibile aprire" />;
  return (
    <EmbedDepth.Provider value={depth + 1}>
      {/* Its OWN runtime — like PortalOverlay — so the embedded page's $page /
          refresh / self-close are isolated from the host page. `input` is the
          read-only `$input` parametrization (ADR-0045 R4 / ADR-0023): the host's
          `page_ref.params` evaluated against its scope, so a detail page reads
          `{{ $input.id }}` in its queries. */}
      <PageRuntimeProvider
        appSlug={appSlug}
        result={data}
        refresh={() => void refetch()}
        input={input}
      >
        <PageRenderer result={data} />
      </PageRuntimeProvider>
    </EmbedDepth.Provider>
  );
}

export function EmbeddedPage({
  slug,
  params,
  rt,
  editing,
}: {
  slug: string;
  /** Evaluated `page_ref.params` → the embedded page's read-only `$input`. */
  params?: Record<string, unknown>;
  rt: PageRuntime | null;
  /** Builder mode (selection chrome) — render a placeholder, not a live fetch. */
  editing?: unknown;
}) {
  const depth = useContext(EmbedDepth);
  const appSlug = rt?.appSlug;
  // No portal session (builder / running-canvas preview / no runtime) → placeholder.
  if (editing || !appSlug || rt.mode === "preview") {
    return <Placeholder slug={slug} note={editing ? undefined : "mostrata nel portale"} />;
  }
  if (depth >= MAX_EMBED_DEPTH) return <Placeholder slug={slug} note="profondità massima" />;
  return <EmbeddedLive appSlug={appSlug} slug={slug} depth={depth} input={params ?? {}} />;
}
